import React, { useEffect, useMemo, useState } from "react"
import { Check, Loader2, X } from "lucide-react"
import ModalOverlay from "../ModalOverlay/ModalOverlay.tsx"
import {
  STARTER_KIT_IMAGES,
  STARTER_KIT_SOURCE_LABEL,
  getStarterKitImageUrl,
  type StarterKitImage,
} from "../../../config/starterKit.ts"
import { urlToFile } from "../../../utils/urlToFile.ts"
import styles from "./StarterKitImagePicker.module.scss"

interface StarterKitImagePickerProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (file: File) => void
  onDeselect?: (fileName: string) => void
  mode?: "single" | "multiple"
  maxImages?: number
  currentImagesCount?: number
  excludeFileNames?: string[]
}

export default function StarterKitImagePicker({
  isOpen,
  onClose,
  onSelect,
  onDeselect,
  mode = "single",
  maxImages,
  currentImagesCount,
  excludeFileNames,
}: StarterKitImagePickerProps) {
  const [loadingId, setLoadingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [localCurrentCount, setLocalCurrentCount] = useState<number>(currentImagesCount ?? 0)
  const [selectedFileNames, setSelectedFileNames] = useState<string[]>(excludeFileNames ?? [])

  useEffect(() => {
    if (!isOpen) return
    setLocalCurrentCount(currentImagesCount ?? 0)
    setSelectedFileNames(excludeFileNames ?? [])
    setError(null)
    setLoadingId(null)
  }, [excludeFileNames, currentImagesCount, isOpen])

  const effectiveMaxImages = typeof maxImages === "number" ? maxImages : 5
  const selectedSet = useMemo(() => new Set(selectedFileNames), [selectedFileNames])
  const remainingToPick = mode === "multiple" ? Math.max(0, effectiveMaxImages - localCurrentCount) : null

  const getStarterFileName = (item: StarterKitImage) => item.path.split("/").pop() || ""

  const handlePick = async (item: StarterKitImage) => {
    const fileName = getStarterFileName(item)
    const name = fileName || "starter-image.jpg"

    if (mode === "multiple" && selectedSet.has(name)) {
      onDeselect?.(name)
      setSelectedFileNames((prev) => prev.filter((n) => n !== name))
      setLocalCurrentCount((prev) => Math.max(0, prev - 1))
      setError(null)
      return
    }

    setError(null)
    setLoadingId(item.id)
    try {
      const url = getStarterKitImageUrl(item)

      if (mode === "multiple") {
        if (localCurrentCount >= effectiveMaxImages) {
          setError(`Máximo ${effectiveMaxImages} imágenes permitidas`)
          return
        }
      }

      const file = await urlToFile(url, name)
      onSelect(file)

      if (mode === "multiple") {
        setSelectedFileNames((prev) => (prev.includes(name) ? prev : [...prev, name]))
        setLocalCurrentCount((prev) => prev + 1)
        return
      }

      onClose()
    } catch (e) {
      setError("No se pudo cargar la imagen. Intena con otra.")
    } finally {
      setLoadingId(null)
    }
  }

  if (!isOpen) return null

  return (
    <ModalOverlay isOpen={true} onClose={onClose} zIndex={4}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>Usar imagen de ejemplo</h2>
          <p className={styles.subtitle}>
            Imágenes de {STARTER_KIT_SOURCE_LABEL}. Elegí una para tu publicación.
          </p>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Cerrar">
            <X size={20} />
          </button>
        </div>
        <div className={styles.content}>
          {error && <p className={styles.error}>{error}</p>}
          {!error && mode === "multiple" && (
            <p className={styles.info}>
              {remainingToPick === 0
                ? `Ya llegaste al máximo de ${effectiveMaxImages} imágenes.`
                : `Te quedan ${remainingToPick} de ${effectiveMaxImages} para seleccionar.`}
            </p>
          )}
          <div className={styles.grid}>
            {STARTER_KIT_IMAGES.map((item) => (
              <button
                key={item.id}
                type="button"
                className={styles.card}
                onClick={() => handlePick(item)}
                disabled={
                  !!loadingId ||
                  (mode === "multiple" &&
                    !selectedSet.has(getStarterFileName(item)) &&
                    localCurrentCount >= effectiveMaxImages)
                }
                aria-pressed={selectedSet.has(getStarterFileName(item))}
              >
                <div className={styles.cardImage}>
                  <img src={getStarterKitImageUrl(item)} alt={item.label || item.id} />
                  {selectedSet.has(getStarterFileName(item)) && (
                    <div className={styles.cardSelectedCheck} aria-hidden="true">
                      <Check size={16} />
                    </div>
                  )}
                  {loadingId === item.id && (
                    <div className={styles.cardLoader}>
                      <Loader2 size={24} className={styles.spinner} />
                    </div>
                  )}
                </div>
                {item.label && <span className={styles.cardLabel}>{item.label}</span>}
              </button>
            ))}
          </div>
        </div>
        {mode === "multiple" && (
          <div className={styles.footer}>
            <button type="button" className={styles.doneBtn} onClick={onClose}>
              Listo ({localCurrentCount}/{effectiveMaxImages})
            </button>
          </div>
        )}
      </div>
    </ModalOverlay>
  )
}
